import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Play, Clock, CheckCircle2, Flame, DoorOpen, MapPin } from "lucide-react";
import {
  useStartCleaningTimer,
  useUpdateUnitEmptyOnArrival,
  type StartLocationSnapshot,
} from "@/hooks/useCurrentCleaningTask";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import {
  AUTO_ALLOW_RADIUS_M,
  haversineDistanceM,
  formatDistanceForDisplay,
  getCurrentPositionWithFallback,
  isGeolocationApiAvailable,
  isGeolocationPositionError,
  locationSignalQuality,
} from "@/lib/geolocation";
import { PreVisitLocationMapPanel } from "@/components/cleaner/PreVisitLocationMapPanel";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface CleaningTimerProps {
  taskId: string;
  propertyId: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMinutes: number | null;
  scheduledDate: string;
  unitEmptyOnArrival?: boolean | null;
  /** Property coords for on-site verification; null if not set */
  propertyLat?: number | null;
  propertyLng?: number | null;
  /** Property address for display in modals */
  propertyAddress?: string | null;
  /**
   * When true and property coords exist: capture GPS first ("Confirm location"), then "Start timer"
   * reuses that snapshot for the start mutation (clearer linear flow).
   */
  confirmLocationStepEnabled?: boolean;
  /**
   * Capture GPS only (no extra-dirty block, no timer start). After confirm, parent advances the flow.
   * Implies the same two-step GPS behavior as confirmLocationStepEnabled when coords exist.
   */
  visitFlowPhase?: "default" | "location_only";
  /** Required when visitFlowPhase is location_only; called after location is confirmed (user taps Continue). */
  onLocationConfirmed?: (snapshot: StartLocationSnapshot) => void;
  /** When true with location_only: hide large hero title (parent shows the pre-visit step heading). */
  embedInPreVisitFlow?: boolean;
  /**
   * When the parent already shows elapsed time (e.g. TurnoverWizard strip), hide the large
   * blue "Time elapsed" card while the visit timer is running.
   */
  hideRunningElapsedCard?: boolean;
}

type UnverifiedReason = "denied" | "error" | "no-property-coords";

type LocationSnapshot = {
  lat: number;
  lng: number;
  accuracyM: number;
  distanceM: number | null;
  verified: boolean;
  capturedAt: string;
  overrideReason?: string | null;
};

const CleaningTimer = ({
  taskId,
  propertyId,
  startedAt,
  completedAt,
  durationMinutes,
  scheduledDate,
  unitEmptyOnArrival,
  propertyLat,
  propertyLng,
  propertyAddress,
  confirmLocationStepEnabled = false,
  visitFlowPhase = "default",
  onLocationConfirmed,
  embedInPreVisitFlow = false,
  hideRunningElapsedCard = false,
}: CleaningTimerProps) => {
  const { t, isRtl } = useTranslation();
  const isLocationCaptureOnly = visitFlowPhase === "location_only";
  const effectiveConfirmLocationStep = confirmLocationStepEnabled || isLocationCaptureOnly;
  const hasCoords =
    propertyLat != null &&
    propertyLng != null &&
    Number.isFinite(propertyLat) &&
    Number.isFinite(propertyLng);
  const showEmbedMapPanel = isLocationCaptureOnly && embedInPreVisitFlow && hasCoords;
  const startTimer = useStartCleaningTimer();
  const updateUnitEmpty = useUpdateUnitEmptyOnArrival();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [extraDirty, setExtraDirty] = useState<"no" | "yes">("no");
  const [extraDirtyNotes, setExtraDirtyNotes] = useState("");
  const [unitEmpty, setUnitEmpty] = useState<"no" | "yes">(unitEmptyOnArrival ? "yes" : "no");
  const [locationLoading, setLocationLoading] = useState(false);
  /** When effectiveConfirmLocationStep: GPS captured here, then Start timer (or parent continue) uses it. */
  const [confirmedLocation, setConfirmedLocation] = useState<LocationSnapshot | null>(null);

  const [liveFix, setLiveFix] = useState<{ lat: number; lng: number; accuracyM: number } | null>(null);
  const [embedSubmitting, setEmbedSubmitting] = useState(false);
  const [embedGpsDenied, setEmbedGpsDenied] = useState(false);
  const [embedRetryBusy, setEmbedRetryBusy] = useState(false);
  const embedWatchIdRef = useRef<number | null>(null);

  const beginEmbedWatch = useCallback((): number | null => {
    if (typeof navigator === "undefined" || !isGeolocationApiAvailable()) return null;
    return navigator.geolocation.watchPosition(
      (pos) => {
        setEmbedGpsDenied(false);
        setLiveFix({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy ?? 0,
        });
      },
      () => {
        setEmbedGpsDenied(true);
        setLiveFix(null);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 45000 }
    );
  }, []);

  useEffect(() => {
    if (!showEmbedMapPanel) return;
    if (typeof window === "undefined") return;
    const host = window.location.hostname;
    if (!window.isSecureContext && host !== "localhost" && host !== "127.0.0.1") {
      toast({
        title: t("error"),
        description: t("geolocationRequiresHttps"),
        variant: "destructive",
      });
      return;
    }
    if (!isGeolocationApiAvailable()) return;

    setEmbedGpsDenied(false);
    if (embedWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(embedWatchIdRef.current);
      embedWatchIdRef.current = null;
    }
    const watchId = beginEmbedWatch();
    embedWatchIdRef.current = watchId;
    return () => {
      if (embedWatchIdRef.current != null) {
        navigator.geolocation.clearWatch(embedWatchIdRef.current);
        embedWatchIdRef.current = null;
      }
    };
  }, [showEmbedMapPanel, propertyLat, propertyLng, t, beginEmbedWatch]);

  const toServerSnapshot = (s: LocationSnapshot): StartLocationSnapshot => ({
    lat: s.lat,
    lng: s.lng,
    accuracyM: s.accuracyM,
    distanceM: s.distanceM,
    verified: s.verified,
    overrideReason: s.overrideReason ?? null,
    capturedAt: s.capturedAt,
  });

  // Far from property modal
  const [farModal, setFarModal] = useState<{
    open: boolean;
    distanceM: number;
    lat: number;
    lng: number;
    accuracyM: number;
    capturedAt: string;
  } | null>(null);
  const [overrideReasonSelect, setOverrideReasonSelect] = useState("");
  const [overrideReasonNote, setOverrideReasonNote] = useState("");

  // Couldn't verify location modal
  const [unverifiedModal, setUnverifiedModal] = useState<{
    open: boolean;
    reason: UnverifiedReason;
    /** If we have position despite "error" (e.g. timeout), we can still save it */
    lat?: number;
    lng?: number;
    accuracyM?: number;
    capturedAt?: string;
  } | null>(null);
  const [unverifiedOverrideNote, setUnverifiedOverrideNote] = useState("");

  useEffect(() => {
    if (startedAt && !completedAt) {
      const startTime = new Date(startedAt).getTime();
      const now = Date.now();
      setElapsedSeconds(Math.floor((now - startTime) / 1000));
    }
  }, [startedAt, completedAt]);

  useEffect(() => {
    if (!startedAt || completedAt) return;

    const interval = setInterval(() => {
      const startTime = new Date(startedAt).getTime();
      const now = Date.now();
      setElapsedSeconds(Math.floor((now - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startedAt, completedAt]);

  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const isEarlyStart = () => {
    const now = new Date();
    const scheduled = new Date(scheduledDate + "T12:00:00");
    return now < scheduled;
  };

  const doStartWithSnapshot = useCallback(
    async (snapshot: {
      lat: number;
      lng: number;
      accuracyM: number;
      distanceM: number | null;
      verified: boolean;
      overrideReason: string | null;
      capturedAt: string;
    }) => {
      try {
        await startTimer.mutateAsync({
          taskId,
          propertyId,
          extraDirty: extraDirty === "yes",
          extraDirtyNotes: extraDirty === "yes" ? extraDirtyNotes || null : null,
          earlyCleaningStarted: isEarlyStart(),
          startLocation: snapshot,
          enforceScheduledDay: true,
        });
        toast({ title: t("timerStarted"), description: t("goodLuck") });
        setFarModal(null);
        setUnverifiedModal(null);
      } catch (error: unknown) {
        console.error("Start timer error:", error);
        const msg = error instanceof Error ? error.message : "";
        if (msg === "CLEANING_START_NOT_SCHEDULED_DAY") {
          toast({
            title: t("cleaningStartNotScheduledDayTitle"),
            description: t("cleaningStartNotScheduledDay"),
            variant: "default",
          });
        } else if (msg === "CLEANING_START_NOT_ASSIGNED_CLEANER") {
          toast({
            title: t("cleaningStartNotAssignedTitle"),
            description: t("cleaningStartNotAssigned"),
            variant: "default",
          });
        } else {
          const anyErr = error as { message?: string };
          toast({
            title: t("error"),
            description: anyErr?.message || t("failedToStartTimer"),
            variant: "destructive",
          });
        }
      }
    },
    [
      taskId,
      propertyId,
      extraDirty,
      extraDirtyNotes,
      scheduledDate,
      startTimer,
      t,
    ]
  );

  const captureLocationForConfirm = async (): Promise<LocationSnapshot | null> => {
    const hasCoords =
      propertyLat != null &&
      propertyLng != null &&
      Number.isFinite(propertyLat) &&
      Number.isFinite(propertyLng);

    if (!hasCoords) {
      setUnverifiedModal({
        open: true,
        reason: "no-property-coords",
      });
      return null;
    }

    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (!window.isSecureContext && host !== "localhost" && host !== "127.0.0.1") {
        toast({
          title: t("error"),
          description: t("geolocationRequiresHttps"),
          variant: "destructive",
        });
        return null;
      }
    }

    if (!isGeolocationApiAvailable()) {
      toast({
        title: t("error"),
        description: t("geolocationNotSupported"),
        variant: "destructive",
      });
      return null;
    }

    setLocationLoading(true);
    try {
      const position = await getCurrentPositionWithFallback();

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const accuracyM = position.coords.accuracy ?? 0;
      const capturedAt = new Date().toISOString();
      const distanceM = haversineDistanceM(lat, lng, propertyLat!, propertyLng!);

      if (distanceM <= AUTO_ALLOW_RADIUS_M) {
        return {
          lat,
          lng,
          accuracyM,
          distanceM,
          verified: true,
          capturedAt,
          overrideReason: null,
        };
      }

      setFarModal({
        open: true,
        distanceM,
        lat,
        lng,
        accuracyM,
        capturedAt,
      });
      return null;
    } catch (err) {
      let description = t("geolocationFailedShort");
      if (isGeolocationPositionError(err)) {
        if (err.code === 1) description = t("geolocationDeniedHint");
        else if (err.code === 3) description = t("geolocationTimeoutHint");
      }
      toast({ title: t("error"), description, variant: "destructive" });
      const reason: UnverifiedReason =
        isGeolocationPositionError(err) && err.code === 1 ? "denied" : "error";
      setUnverifiedModal({
        open: true,
        reason,
      });
      return null;
    } finally {
      setLocationLoading(false);
    }
  };

  const handleConfirmLocationOnly = async () => {
    const snap = await captureLocationForConfirm();
    if (snap) setConfirmedLocation(snap);
  };

  const handleStartTimer = async () => {
    const hasCoords =
      propertyLat != null &&
      propertyLng != null &&
      Number.isFinite(propertyLat) &&
      Number.isFinite(propertyLng);

    if (!hasCoords) {
      setUnverifiedModal({
        open: true,
        reason: "no-property-coords",
      });
      return;
    }

    if (effectiveConfirmLocationStep && confirmedLocation) {
      await doStartWithSnapshot({
        lat: confirmedLocation.lat,
        lng: confirmedLocation.lng,
        accuracyM: confirmedLocation.accuracyM,
        distanceM: confirmedLocation.distanceM,
        verified: confirmedLocation.verified,
        overrideReason: confirmedLocation.overrideReason ?? null,
        capturedAt: confirmedLocation.capturedAt,
      });
      setConfirmedLocation(null);
      return;
    }

    if (effectiveConfirmLocationStep) {
      toast({
        title: t("error"),
        description: "Confirm your location first, then start the timer.",
        variant: "destructive",
      });
      return;
    }

    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (!window.isSecureContext && host !== "localhost" && host !== "127.0.0.1") {
        toast({
          title: t("error"),
          description: t("geolocationRequiresHttps"),
          variant: "destructive",
        });
        return;
      }
    }
    if (!isGeolocationApiAvailable()) {
      toast({
        title: t("error"),
        description: t("geolocationNotSupported"),
        variant: "destructive",
      });
      return;
    }

    setLocationLoading(true);
    try {
      const position = await getCurrentPositionWithFallback();

      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const accuracyM = position.coords.accuracy ?? 0;
      const capturedAt = new Date().toISOString();
      const distanceM = haversineDistanceM(lat, lng, propertyLat!, propertyLng!);

      if (distanceM <= AUTO_ALLOW_RADIUS_M) {
        await doStartWithSnapshot({
          lat,
          lng,
          accuracyM,
          distanceM,
          verified: true,
          overrideReason: null,
          capturedAt,
        });
        return;
      }

      setFarModal({
        open: true,
        distanceM,
        lat,
        lng,
        accuracyM,
        capturedAt,
      });
    } catch (err) {
      let description = t("geolocationFailedShort");
      if (isGeolocationPositionError(err)) {
        if (err.code === 1) description = t("geolocationDeniedHint");
        else if (err.code === 3) description = t("geolocationTimeoutHint");
      }
      toast({ title: t("error"), description, variant: "destructive" });
      const reason: UnverifiedReason =
        isGeolocationPositionError(err) && err.code === 1 ? "denied" : "error";
      setUnverifiedModal({
        open: true,
        reason,
      });
    } finally {
      setLocationLoading(false);
    }
  };

  const handleEmbedRetryRefetch = async () => {
    if (embedRetryBusy || !showEmbedMapPanel) return;
    setEmbedRetryBusy(true);
    if (embedWatchIdRef.current != null) {
      navigator.geolocation.clearWatch(embedWatchIdRef.current);
      embedWatchIdRef.current = null;
    }
    try {
      const p = await getCurrentPositionWithFallback();
      setLiveFix({
        lat: p.coords.latitude,
        lng: p.coords.longitude,
        accuracyM: p.coords.accuracy ?? 0,
      });
      setEmbedGpsDenied(false);
      toast({
        title: t("preVisitRetrySuccessTitle"),
        description: t("preVisitRetrySuccessHint"),
      });
    } catch (err) {
      let description = t("geolocationFailedShort");
      if (isGeolocationPositionError(err)) {
        if (err.code === 1) description = t("geolocationDeniedHint");
        else if (err.code === 3) description = t("geolocationTimeoutHint");
      }
      toast({ title: t("error"), description, variant: "destructive" });
    } finally {
      if (showEmbedMapPanel && typeof navigator !== "undefined" && isGeolocationApiAvailable()) {
        const w = beginEmbedWatch();
        embedWatchIdRef.current = w;
      }
      setEmbedRetryBusy(false);
    }
  };

  const submitEmbedLocation = async (verified: boolean, overrideReason: string | null) => {
    if (!liveFix || propertyLat == null || propertyLng == null) return;
    const distanceM = haversineDistanceM(liveFix.lat, liveFix.lng, propertyLat, propertyLng);
    const snap: LocationSnapshot = {
      lat: liveFix.lat,
      lng: liveFix.lng,
      accuracyM: liveFix.accuracyM,
      distanceM,
      verified,
      capturedAt: new Date().toISOString(),
      overrideReason,
    };
    setEmbedSubmitting(true);
    try {
      await Promise.resolve(onLocationConfirmed?.(toServerSnapshot(snap)));
    } catch (e) {
      console.error(e);
      toast({
        title: t("error"),
        description: t("preVisitConfirmSubmitError"),
        variant: "destructive",
      });
    } finally {
      setEmbedSubmitting(false);
    }
  };

  const handleGetDirections = () => {
    // Open Google Maps directions: from current location to property (address or lat,lng)
    const destination = propertyAddress?.trim()
      ? encodeURIComponent(propertyAddress.trim())
      : propertyLat != null && propertyLng != null
        ? `${propertyLat},${propertyLng}`
        : null;
    if (!destination) return;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStartAnywayFromFar = async () => {
    const reason =
      overrideReasonSelect === "other"
        ? overrideReasonNote.trim() || "Other"
        : overrideReasonSelect || overrideReasonNote.trim();
    if (!reason) {
      toast({
        title: t("error"),
        description: "Please select or enter a reason to start anyway.",
        variant: "destructive",
      });
      return;
    }
    if (!farModal) return;
    if (effectiveConfirmLocationStep && !startedAt) {
      setConfirmedLocation({
        lat: farModal.lat,
        lng: farModal.lng,
        accuracyM: farModal.accuracyM,
        distanceM: farModal.distanceM,
        verified: false,
        overrideReason: reason,
        capturedAt: farModal.capturedAt,
      });
      setFarModal(null);
      setOverrideReasonSelect("");
      setOverrideReasonNote("");
      return;
    }
    await doStartWithSnapshot({
      lat: farModal.lat,
      lng: farModal.lng,
      accuracyM: farModal.accuracyM,
      distanceM: farModal.distanceM,
      verified: false,
      overrideReason: reason,
      capturedAt: farModal.capturedAt,
    });
    setOverrideReasonSelect("");
    setOverrideReasonNote("");
  };

  const handleStartAnywayFromUnverified = async () => {
    if (!unverifiedModal) return;
    const reason =
      unverifiedModal.reason +
      (unverifiedOverrideNote.trim() ? ": " + unverifiedOverrideNote.trim() : "");
    if (
      effectiveConfirmLocationStep &&
      !startedAt &&
      unverifiedModal.lat != null &&
      unverifiedModal.lng != null
    ) {
      setConfirmedLocation({
        lat: unverifiedModal.lat,
        lng: unverifiedModal.lng,
        accuracyM: unverifiedModal.accuracyM ?? 0,
        distanceM: null,
        verified: false,
        overrideReason: reason,
        capturedAt: unverifiedModal.capturedAt ?? new Date().toISOString(),
      });
      setUnverifiedModal(null);
      setUnverifiedOverrideNote("");
      return;
    }
    await doStartWithSnapshot({
      lat: unverifiedModal.lat ?? null,
      lng: unverifiedModal.lng ?? null,
      accuracyM: unverifiedModal.accuracyM ?? null,
      distanceM: null,
      verified: false,
      overrideReason: reason,
      capturedAt: unverifiedModal.capturedAt ?? new Date().toISOString(),
    });
    setUnverifiedOverrideNote("");
  };

  const handleUnitEmptyChange = async (value: "yes" | "no") => {
    setUnitEmpty(value);
    if (value === "yes" && !unitEmptyOnArrival) {
      try {
        await updateUnitEmpty.mutateAsync({ taskId, propertyId, unitEmptyOnArrival: true });
        toast({ title: t("recorded"), description: t("unitEmptyStatusSaved") });
      } catch (error: any) {
        console.error("Update unit empty error:", error);
        setUnitEmpty("no");
      }
    }
  };

  const embedDistanceM = useMemo(() => {
    if (!showEmbedMapPanel || !liveFix) return null;
    return haversineDistanceM(liveFix.lat, liveFix.lng, propertyLat!, propertyLng!);
  }, [showEmbedMapPanel, liveFix, propertyLat, propertyLng]);

  const embedSignal = useMemo(() => {
    if (embedDistanceM == null || liveFix == null) {
      return { goodSignal: false, canConfirmVerified: false };
    }
    return locationSignalQuality(embedDistanceM, liveFix.accuracyM);
  }, [embedDistanceM, liveFix]);

  if (!startedAt) {
    const showTwoStep = effectiveConfirmLocationStep && hasCoords;

    const showIntroBlock = !isLocationCaptureOnly || !embedInPreVisitFlow;

    return (
      <>
      <Card
        className={cn(
          "mb-4 sm:mb-6",
          isLocationCaptureOnly && embedInPreVisitFlow
            ? "border bg-card shadow-sm"
            : "border-primary/20 bg-primary/5"
        )}
        dir={isRtl ? "rtl" : "ltr"}
      >
        <CardContent className="py-4 sm:py-6">
          <div className="text-center">
            {showIntroBlock ? (
              <>
            <Clock className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-primary mb-2 sm:mb-3" />
            <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">
              {isLocationCaptureOnly ? t("onSiteCheckTitle") : t("startCleaningQuestion")}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mb-4">
              {isLocationCaptureOnly ? t("onSiteCheckHint") : t("timerWillStart")}
            </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mb-4 text-center sm:text-left max-w-md mx-auto sm:mx-0 leading-relaxed">
                {t("preVisitLocationCardHint")}
              </p>
            )}
            
            {!isLocationCaptureOnly && (
            <div className={`mb-4 p-3 sm:p-4 bg-background rounded-lg border w-full ${isRtl ? 'text-right' : 'text-left'}`}>
              <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                <Flame className="w-4 h-4 text-orange-500" />
                {t("extraDirty")}
              </Label>
              <Select value={extraDirty} onValueChange={(v) => setExtraDirty(v as "no" | "yes")}>
                <SelectTrigger className="w-full h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="no" className="h-12 text-base">{t("no")}</SelectItem>
                  <SelectItem value="yes" className="h-12 text-base">{t("yes")}</SelectItem>
                </SelectContent>
              </Select>
              
              {extraDirty === "yes" && (
                <div className="mt-3">
                  <Label className="text-xs sm:text-sm text-muted-foreground">
                    {t("extraDirtyNotes")}
                  </Label>
                  <Textarea
                    value={extraDirtyNotes}
                    onChange={(e) => setExtraDirtyNotes(e.target.value)}
                    placeholder={t("extraDirtyPlaceholder")}
                    className="mt-1 text-base"
                    rows={2}
                  />
                </div>
              )}
            </div>
            )}

            {embedGpsDenied && showEmbedMapPanel && (
              <div className="space-y-3 text-left">
                <Alert variant="destructive">
                  <AlertDescription>{t("preVisitGpsDeniedHint")}</AlertDescription>
                </Alert>
                <Button type="button" variant="outline" className="w-full" onClick={() => void handleEmbedRetryRefetch()}>
                  {t("preVisitRetry")}
                </Button>
              </div>
            )}

            {showEmbedMapPanel && !embedGpsDenied ? (
              <div className="text-left">
                <PreVisitLocationMapPanel
                  propertyLat={propertyLat!}
                  propertyLng={propertyLng!}
                  liveFix={liveFix}
                  isLocating={!liveFix}
                  distanceM={embedDistanceM}
                  goodSignal={embedSignal.goodSignal}
                  canConfirmVerified={embedSignal.canConfirmVerified}
                  isFar={embedDistanceM != null && embedDistanceM > AUTO_ALLOW_RADIUS_M}
                  isSubmitting={embedSubmitting}
                  isRetrying={embedRetryBusy}
                  onRetry={() => void handleEmbedRetryRefetch()}
                  onRecenter={() => void handleEmbedRetryRefetch()}
                  onConfirmVerified={() => void submitEmbedLocation(true, null)}
                  onConfirmAnyway={() => {
                    const far =
                      embedDistanceM != null && embedDistanceM > AUTO_ALLOW_RADIUS_M;
                    void submitEmbedLocation(
                      false,
                      far
                        ? "far_from_property_confirm_anyway"
                        : "low_gps_accuracy_confirm_anyway"
                    );
                  }}
                  onGetDirections={handleGetDirections}
                  t={t}
                />
              </div>
            ) : !showEmbedMapPanel && showTwoStep && confirmedLocation ? (
              <div className="space-y-3">
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                  <p className="font-medium">{t("locationSaved") ?? "Location saved"}</p>
                  {confirmedLocation.distanceM != null && (
                    <p className="text-xs text-green-800 mt-1">
                      {formatDistanceForDisplay(confirmedLocation.distanceM)}
                      {confirmedLocation.verified ? "" : ` · ${t("overridePending") ?? "Override noted"}`}
                    </p>
                  )}
                </div>
                {isLocationCaptureOnly ? (
                  <Button
                    type="button"
                    onClick={() => {
                      onLocationConfirmed?.(toServerSnapshot(confirmedLocation));
                      setConfirmedLocation(null);
                    }}
                    disabled={!onLocationConfirmed}
                    size="lg"
                    className="w-full h-14 text-base font-semibold"
                  >
                    {t("continue")}
                  </Button>
                ) : (
                  <Button
                    onClick={handleStartTimer}
                    disabled={startTimer.isPending || locationLoading}
                    size="lg"
                    className="w-full h-14 text-base font-semibold"
                  >
                    <Play className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
                    {locationLoading ? "…" : t("startTimer")}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setConfirmedLocation(null)}
                >
                  {t("recheckLocation") ?? "Re-check location"}
                </Button>
              </div>
            ) : !showEmbedMapPanel && showTwoStep ? (
              <Button
                onClick={handleConfirmLocationOnly}
                disabled={startTimer.isPending || locationLoading}
                size="lg"
                className="w-full h-14 text-base font-semibold"
              >
                <MapPin className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
                {locationLoading ? "Checking location…" : t("confirmLocation") ?? "Confirm location"}
              </Button>
            ) : isLocationCaptureOnly ? (
              <Button
                onClick={handleConfirmLocationOnly}
                disabled={startTimer.isPending || locationLoading}
                size="lg"
                className="w-full h-14 text-base font-semibold"
              >
                <MapPin className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
                {locationLoading ? "Checking location…" : t("confirmLocation") ?? "Confirm location"}
              </Button>
            ) : (
            <Button
              onClick={handleStartTimer}
              disabled={startTimer.isPending || locationLoading}
              size="lg"
              className="w-full h-14 text-base font-semibold"
            >
              <Play className={`w-5 h-5 ${isRtl ? "ml-2" : "mr-2"}`} />
              {locationLoading ? "Checking location…" : t("startTimer")}
            </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Far from property modal */}
      <Dialog
        open={!!farModal?.open}
        onOpenChange={(open) => !open && setFarModal(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>You seem far from the property</DialogTitle>
            <DialogDescription>
              You're about {formatDistanceForDisplay(farModal?.distanceM ?? 0)} away.
              {propertyAddress && (
                <span className="mt-2 block text-foreground">{propertyAddress}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Reason to start anyway (required)</Label>
            <Select
              value={overrideReasonSelect}
              onValueChange={setOverrideReasonSelect}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on_my_way">I'm on my way</SelectItem>
                <SelectItem value="starting_remotely">Starting remotely</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {(overrideReasonSelect === "other" || !overrideReasonSelect) && (
              <Textarea
                placeholder="Short note (required if Other)"
                value={overrideReasonNote}
                onChange={(e) => setOverrideReasonNote(e.target.value)}
                rows={2}
              />
            )}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={handleGetDirections} className="w-full sm:w-auto">
              <MapPin className="w-4 h-4 mr-2" />
              Get directions
            </Button>
            <Button
              onClick={handleStartAnywayFromFar}
              disabled={startTimer.isPending}
              className="w-full sm:w-auto"
            >
              Start anyway
            </Button>
            <Button
              variant="ghost"
              onClick={() => setFarModal(null)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Couldn't verify location modal */}
      <Dialog
        open={!!unverifiedModal?.open}
        onOpenChange={(open) => !open && setUnverifiedModal(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>We couldn't verify your location</DialogTitle>
            <DialogDescription>
              {unverifiedModal?.reason === "denied" &&
                "Location access was denied. You can still start the task."}
              {unverifiedModal?.reason === "error" &&
                "Location could not be determined. You can still start the task."}
              {unverifiedModal?.reason === "no-property-coords" &&
                "This property has no address coordinates. You can still start the task."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-muted-foreground">Optional note</Label>
            <Textarea
              placeholder="Add a note (optional)"
              value={unverifiedOverrideNote}
              onChange={(e) => setUnverifiedOverrideNote(e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={handleStartAnywayFromUnverified}
              disabled={startTimer.isPending}
            >
              Start anyway
            </Button>
            <Button variant="ghost" onClick={() => setUnverifiedModal(null)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  if (!completedAt) {
    return (
      <div className="space-y-4 mb-4 sm:mb-6" dir={isRtl ? "rtl" : "ltr"}>
        {!hideRunningElapsedCard ? (
          <Card className="border-blue-200 bg-blue-50 sticky top-0 z-10">
            <CardContent className="py-3 sm:py-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse shrink-0" />
                  <span className="text-blue-800 font-medium text-sm sm:text-base">{t("timeElapsed")}:</span>
                </div>
                <span className="text-2xl sm:text-3xl font-mono font-bold text-blue-900 shrink-0 tabular-nums">
                  {formatTime(elapsedSeconds)}
                </span>
              </div>
              <p className="text-xs text-blue-800/90 leading-snug">{t("timeElapsedHint")}</p>
            </CardContent>
          </Card>
        ) : null}

        {/* Unit Empty on Arrival Toggle - Only show if not already marked */}
        {!unitEmptyOnArrival && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-3 sm:py-4">
              <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                <DoorOpen className="w-4 h-4 text-amber-600" />
                {t("unitEmptyQuestion")}
              </Label>
              <Select value={unitEmpty} onValueChange={(v) => handleUnitEmptyChange(v as "no" | "yes")}>
                <SelectTrigger className="w-full h-10 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="no" className="h-10 text-base">{t("noUnitWasOccupied")}</SelectItem>
                  <SelectItem value="yes" className="h-10 text-base">{t("yesGuestLeftEarly")}</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <Card className="mb-4 sm:mb-6 border-green-200 bg-green-50" dir={isRtl ? "rtl" : "ltr"}>
      <CardContent className="py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium text-sm sm:text-base">{t("done")}:</span>
          </div>
          <span className="text-xl sm:text-2xl font-bold text-green-900">
            {durationMinutes !== null ? `${durationMinutes} ${t("min")}` : "N/A"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CleaningTimer;